import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { trackEvent } from "../utils/mixpanel";

const ImagePreviewModal = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onNavigate,
  hotspotId,
}) => {
  const navigateWithTracking = (direction) => {
    if (!images?.length) return;

    const isPrev = direction === "previous";
    const isNext = direction === "next";
    if (!isPrev && !isNext) return;

    const newIndex = isPrev ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex > images.length - 1) return;

    onNavigate(newIndex);
    trackEvent("wall_gallery_image_navigation", {
      hotspot_id: hotspotId,
      direction,
      from_index: currentIndex,
      to_index: newIndex,
      total_images: images.length,
    });
  };

  // Handle keyboard events (Escape to close, arrows to navigate)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft" && currentIndex > 0) {
        navigateWithTracking("previous");
      } else if (e.key === "ArrowRight" && currentIndex < images.length - 1) {
        navigateWithTracking("next");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, images, onClose, onNavigate]);

  // Track image preview opened
  useEffect(() => {
    if (isOpen) {
      trackEvent("wall_gallery_image_previewed", {
        hotspot_id: hotspotId,
        image_index: currentIndex,
        image_url: images?.[currentIndex],
        total_images: images.length,
      });
    }
  }, [isOpen, currentIndex, hotspotId, images.length]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop, not on the image
    if (e.target.classList.contains("image-preview-backdrop")) {
      onClose();
    }
  };

  const handlePrevious = () => {
    navigateWithTracking("previous");
  };

  const handleNext = () => {
    navigateWithTracking("next");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="image-preview-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          {/* Close Button */}
          <button className="image-preview-close" onClick={onClose}>
            <X size={24} />
          </button>

          {/* Image Counter */}
          <div className="image-preview-counter">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Main Image Container */}
          <motion.div
            className="image-preview-container"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={4}
              centerOnInit
              wheel={{ step: 0.1 }}
              doubleClick={{ mode: "toggle" }}
              pinch={{ step: 5 }}
            >
              <TransformComponent
                wrapperClass="image-preview-transform-wrapper"
                contentClass="image-preview-transform-content"
              >
                <img
                  src={images[currentIndex]}
                  alt={`Gallery image ${currentIndex + 1}`}
                  className="image-preview-image"
                />
              </TransformComponent>
            </TransformWrapper>
          </motion.div>

          {/* Navigation Buttons */}
          {currentIndex > 0 && (
            <button
              className="image-preview-nav image-preview-nav-prev"
              onClick={handlePrevious}
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {currentIndex < images.length - 1 && (
            <button
              className="image-preview-nav image-preview-nav-next"
              onClick={handleNext}
            >
              <ChevronRight size={32} />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImagePreviewModal;
